// CommentForm.js
import { useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { Button, Alert } from "react-bootstrap";
import API from "../../../../api";
import ProfileImage from "../../../../components/ProfileImage";
import RichText from "../../../../components/RichText";
import CommentEditor from "../../../../components/CommentEditor";
import { normalizeQuillHtml, richTextHasText } from "../../../../components/QuillInput";

// If you wired the shim: import { useAuth } from "../../../AuthUser";
// Otherwise, point directly at your provider:
import { useAuth } from "../../../Auth/AuthProvider";

function CommentForm() {
	const { slug } = useParams();
	const { user } = useAuth() || {}; // guard if context not found
	const [result, setResult] = useState("");
	const [disabled, setDisabled] = useState(false);
	const [isSubmitting, setSubmitting] = useState(false);
	const [text, setText] = useState("");

	// Logged out?
	if (!user) {
		return (
			<Alert variant="warning">
				Please <a href="/signin">sign in</a> to comment.
			</Alert>
		);
	}

	// Not premium?
	const isPremium = !!user?.planType;
	if (!isPremium) {
		return (
			<Alert variant="danger">
				You must be a premium member to comment. <a href="/pricing">GET ACCESS NOW</a>
			</Alert>
		);
	}

	const displayName =
		user?.firstName?.trim() ||
		(typeof user?.id === "number" ? `user00${user.id + user.id}` : "Member");

	const handleSubmit = async (e) => {
		e.preventDefault();
		// An "empty" Quill document is "<p><br></p>", so a truthy/trim check on
		// the value would happily post a blank comment.
		if (!richTextHasText(text)) return;
		// Quill serializes every space as &nbsp;, which blocks wrapping wherever
		// the body is rendered later. Normalize on submit, never in onChange.
		const body = normalizeQuillHtml(text);

		try {
			setSubmitting(true);
			setDisabled(true);
			await API.post(`videoComments/${slug}`, {
				comment: body,
				bodyFormat: "html",
				creator: user.id, // safe: only runs when user exists (see guards above)
			});
			setResult(body);
			setText("");
		} finally {
			setSubmitting(false);
			// keep disabled after first comment per your previous behavior
			setDisabled(true);
		}
	};

	const commentPreview = (commentText) => (
		<div className="mt-5 comment-container">
			<div className="comments">
				<div className="d-flex flex-row p-3 gap-3">
					<ProfileImage size={40} textSizeRatio={2.75} text={user?.email || "Member"} />
					<div className="w-100">
						<div className="d-flex justify-content-between align-items-center">
							<div className="d-flex flex-row align-items-center">
								<span className="me-2">{displayName}</span>
								<small className="text-orange">
									<strong>{user?.admin ? "Master Instructor" : ""}</strong>
								</small>
							</div>
							<small>{moment().fromNow()}</small>
						</div>
						<div className="text-justify comment-text mb-0"><RichText text={commentText} format="html"/></div>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<>
			<form className="mb-5" onSubmit={handleSubmit}>
				<div className="mt-3 d-flex flex-row align-items-center gap-3">
					<CommentEditor
						value={text}
						onChange={setText}
						placeholder="Enter your comment..."
						readOnly={disabled || isSubmitting}
					/>
				</div>
				<div className="mt-2 float-end">
					<Button size="sm" variant="primary" type="submit" disabled={disabled || isSubmitting}>
						{isSubmitting ? (
							<>
								<span className="spinner-border spinner-border-sm" role="status" aria-hidden={true} />{" "}
							</>
						) : (
							"Comment"
						)}
					</Button>
				</div>
			</form>

			{result ? commentPreview(result) : null}
		</>
	);
}

export default CommentForm;
